import React, { useEffect, useRef, useState } from "react";
import {
  BarChart3, Brain, Clock, FileText, Globe, MessageSquare, Send, Sparkles, Star, Zap,
} from "lucide-react";
import { api } from "../lib/api";
import useCustomerData from "../hooks/useCustomerData";
import { STATUS, Section, KpiCard, InsightsGrid, ModuleHeader } from "../components/intel/IntelKit";

// ---------------------------------------------------------------------------
// SECTION 1 — KPIs
// ---------------------------------------------------------------------------
const KPIS = [
  { id: "queries",   label: "AI Queries / Month", value: "2,184", sub: "+34% vs last month",     status: "green", icon: MessageSquare },
  { id: "response",  label: "Avg Response",       value: "1.8s",  sub: "p95 under 4s",            status: "green", icon: Zap },
  { id: "languages", label: "Languages",          value: "3",     sub: "English · हिन्दी · मराठी", status: "blue",  icon: Globe },
  { id: "docqa",     label: "Document Q&A",       value: "Active",sub: "PDFs · Tally · Excel",    status: "green", icon: FileText },
  { id: "models",    label: "AI Models",          value: "4",     sub: "GLM 4.7 · Whisper · Vision · Embed", status: "blue", icon: Brain },
  { id: "csat",      label: "Satisfaction",       value: "4.8/5", sub: "from 487 ratings",        status: "green", icon: Star },
];

// ---------------------------------------------------------------------------
// SECTION 2 — Capability matrix
// ---------------------------------------------------------------------------
const STAT_PILL = {
  live:       { label: "Live",       chip: "bg-emerald-100 text-emerald-700" },
  beta:       { label: "Beta",       chip: "bg-amber-100 text-amber-700"     },
  enterprise: { label: "Enterprise", chip: "bg-blue-100 text-blue-700"       },
};

const CAPS = [
  { id: "fin_analysis", capability: "Real-time financial analysis & ratio calculation",  status: "live",       module: "All" },
  { id: "cashflow",     capability: "Cash flow forecasting (30/60/90 days)",              status: "live",       module: "CFO" },
  { id: "margin",       capability: "Margin variance & job-card analysis",                status: "live",       module: "CFO" },
  { id: "okr",          capability: "OKR scoring & strategic scenario modelling",         status: "live",       module: "CEO" },
  { id: "churn",        capability: "Customer churn prediction & NPS-driven referrals",   status: "live",       module: "CMO" },
  { id: "audit",        capability: "Software audit & IT spend optimisation",             status: "live",       module: "CTO" },
  { id: "wa_qa",        capability: "WhatsApp-native Q&A in Hindi & Marathi",             status: "beta",       module: "All" },
  { id: "gst",          capability: "GSTR-3B / 2B reconciliation reasoning",              status: "beta",       module: "CFO" },
  { id: "voice",        capability: "Voice-to-board-report draft (5 min Hindi audio)",    status: "beta",       module: "All" },
  { id: "stream",       capability: "Live ERP-stream anomaly detection",                  status: "beta",       module: "CTO" },
  { id: "consol",       capability: "Multi-entity consolidation reasoning",               status: "enterprise", module: "All" },
  { id: "mca",          capability: "Auto-fill MCA / RoC filings from Tally",             status: "enterprise", module: "CFO" },
];

function CapabilityMatrix() {
  return (
    <Section testid="adv-cap-matrix" title="Capability matrix" subtitle="12 rows · Status: Live / Beta / Enterprise">
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-left text-[10.5px] uppercase tracking-wider text-slate-500">
              <th className="py-2 pr-3">Capability</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2">Module</th>
            </tr>
          </thead>
          <tbody>
            {CAPS.map((c) => {
              const p = STAT_PILL[c.status];
              return (
                <tr key={c.id} data-testid={`adv-cap-${c.id}`} className="border-t border-slate-100">
                  <td className="py-2 pr-3 font-medium text-[#0F1B2D]">{c.capability}</td>
                  <td className="py-2 pr-3"><span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${p.chip}`}>{p.label}</span></td>
                  <td className="py-2 text-slate-600">{c.module}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// SECTION 3 — Multilingual chat (custom variant with suggestion chips)
// ---------------------------------------------------------------------------
const SUGGESTIONS = [
  "Compare EBITDA to industry peers",
  "Draft board report",
  "Translate MIS to Hindi",
  "Project cash for next 30 days",
  "What are my biggest growth risks?",
];

function Typing() {
  return (
    <div data-testid="adv-chat-typing" className="max-w-[85%] rounded-2xl bg-[#EFF6FF] px-4 py-3">
      <div className="flex items-center gap-1">
        {[0, 200, 400].map((d) => (
          <span key={d} className="h-1.5 w-1.5 rounded-full bg-slate-400 core-pulse" style={{ animationDelay: `${d}ms` }} />
        ))}
      </div>
    </div>
  );
}

function MultilingualChat({ }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sid = useRef(`adv-${Math.random().toString(36).slice(2, 10)}`);
  const scroll = useRef(null);

  useEffect(() => { if (scroll.current) scroll.current.scrollTop = scroll.current.scrollHeight; }, [messages, sending]);

  const send = async (text) => {
    const t = (text ?? input).trim();
    if (!t || sending) return;
    setMessages((m) => [...m, { role: "user", text: t }]);
    setInput("");
    setSending(true);
    const res = await api.advancedAiChat(t, sid.current);
    const reply = res.ok
      ? (res.data?.reply || "Sorry, I could not process that right now. Please try again in a moment.")
      : (res.error || "Sorry, I could not process that right now. Please try again in a moment.");
    setMessages((m) => [...m, { role: "ai", text: reply }]);
    setSending(false);
  };

  return (
    <section data-testid="adv-chat" className="rounded-xl border border-slate-200 bg-white shadow-card">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
        <div>
          <div className="text-[14px] font-bold text-[#1E3A5F]">Ask C-ORE (Multilingual)</div>
          <div className="text-[12px] text-slate-500">Routes to CEO · CFO · CMO · CTO automatically · English / हिन्दी / मराठी</div>
        </div>
        <Sparkles size={16} className="text-emerald-600" />
      </div>
      <div ref={scroll} className="space-y-2 overflow-y-auto px-5 py-3" style={{ maxHeight: 320 }}>
        {messages.length === 0 && !sending && (
          <div data-testid="adv-chat-empty" className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-center text-[12px] text-slate-500">
            Ask anything in English, हिन्दी, or मराठी. Pick a suggestion below or type your own question.
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} data-testid={`adv-chat-msg-${i}`}
            className={`max-w-[88%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-[12.5px] leading-snug ${m.role === "ai" ? "bg-[#EFF6FF] text-[#0F1B2D]" : "ml-auto bg-emerald-600 text-white"}`}>
            {m.text}
          </div>
        ))}
        {sending && <Typing />}
      </div>

      {/* Suggestion chips */}
      <div data-testid="adv-suggestions" className="flex flex-wrap gap-2 border-t border-slate-100 px-5 py-2.5">
        {SUGGESTIONS.map((s) => (
          <button key={s} data-testid={`adv-suggest-${s.replace(/\s+/g, "-").toLowerCase()}`}
            onClick={() => send(s)} disabled={sending}
            className="rounded-full border border-blue-200 bg-blue-50/60 px-2.5 py-1 text-[11.5px] font-medium text-blue-700 hover:border-blue-400 hover:bg-blue-100 disabled:opacity-50">
            {s}
          </button>
        ))}
      </div>

      <div className="border-t border-slate-100 px-5 py-3">
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2">
          <input data-testid="adv-chat-input" type="text" value={input} onChange={(e) => setInput(e.target.value)}
            placeholder={sending ? "Thinking…" : "Ask anything in English, हिन्दी, or मराठी"}
            disabled={sending}
            className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[12.5px] text-[#0F1B2D] placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none disabled:bg-slate-50"
          />
          <button type="submit" data-testid="adv-chat-send" disabled={sending || !input.trim()}
            className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-2 text-[12.5px] font-semibold text-white hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <Send size={13} /> Send
          </button>
        </form>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// SECTION 4 — Usage breakdown bars
// ---------------------------------------------------------------------------
const USAGE = [
  { label: "CFO queries (financial)",   pct: 38, count: 830, tone: "green" },
  { label: "CTO queries (tech / IT)",   pct: 24, count: 524, tone: "blue"  },
  { label: "CEO queries (strategy)",    pct: 21, count: 459, tone: "amber" },
  { label: "CMO queries (marketing)",   pct: 17, count: 371, tone: "blue"  },
];

function UsageBreakdown({ }) {
  // Active customer — usage telemetry not yet wired into Supabase, so dash everything.
  if (true) {
    return (
      <Section testid="adv-usage" title="Usage by C-Suite persona" subtitle="Begins tracking once you start asking questions in this module">
        <ul className="space-y-2.5">
          {[
            { label: "CFO queries (financial)", tone: "green" },
            { label: "CTO queries (tech / IT)", tone: "blue"  },
            { label: "CEO queries (strategy)",  tone: "amber" },
            { label: "CMO queries (marketing)", tone: "blue"  },
          ].map((u) => {
            const s = STATUS[u.tone];
            return (
              <li key={u.label} data-testid={`adv-usage-${u.label.replace(/\s+/g, "-").toLowerCase()}`} className="flex items-center gap-3">
                <span className="w-[100px] tablet:w-[150px] desktop:w-[200px] shrink-0 text-[12px] font-medium text-[#0F1B2D]">{u.label}</span>
                <div className="h-2 flex-1 rounded-full bg-slate-100">
                  <div className={`h-2 rounded-full ${s.bar}`} style={{ width: "0%" }} />
                </div>
                <span className="w-16 text-right text-[12px] tabular-nums text-slate-400">—</span>
                <span className="w-10 text-right text-[12px] font-semibold tabular-nums text-slate-400">—</span>
              </li>
            );
          })}
        </ul>
        <div className="mt-3 grid grid-cols-2 gap-3 rounded-lg bg-slate-50 px-4 py-3 text-[11.5px] desktop:grid-cols-4">
          <div><div className="text-slate-500">Total queries</div><div className="font-bold text-slate-400">—</div></div>
          <div><div className="text-slate-500">Avg response</div><div className="font-bold text-slate-400">—</div></div>
          <div><div className="text-slate-500">Hindi share</div><div className="font-bold text-slate-400">—</div></div>
          <div><div className="text-slate-500">Marathi share</div><div className="font-bold text-slate-400">—</div></div>
        </div>
      </Section>
    );
  }

  return (
    <Section testid="adv-usage" title="Usage by C-Suite persona" subtitle="2,184 queries this month · classifier accuracy 96%">
      <ul className="space-y-2.5">
        {USAGE.map((u) => {
          const s = STATUS[u.tone];
          return (
            <li key={u.label} data-testid={`adv-usage-${u.label.replace(/\s+/g, "-").toLowerCase()}`} className="flex items-center gap-3">
              <span className="w-[100px] tablet:w-[150px] desktop:w-[200px] shrink-0 text-[12px] font-medium text-[#0F1B2D]">{u.label}</span>
              <div className="h-2 flex-1 rounded-full bg-slate-100">
                <div className={`h-2 rounded-full ${s.bar}`} style={{ width: `${u.pct}%`, transition: "width 700ms ease" }} />
              </div>
              <span className="w-16 text-right text-[12px] tabular-nums text-[#0F1B2D]">{u.count}</span>
              <span className="w-10 text-right text-[12px] font-semibold tabular-nums text-slate-700">{u.pct}%</span>
            </li>
          );
        })}
      </ul>
      <div className="mt-3 grid grid-cols-2 gap-3 rounded-lg bg-slate-50 px-4 py-3 text-[11.5px] desktop:grid-cols-4">
        <div><div className="text-slate-500">Total queries</div><div className="font-bold text-[#0F1B2D]">2,184</div></div>
        <div><div className="text-slate-500">Avg response</div><div className="font-bold text-emerald-700">1.8s</div></div>
        <div><div className="text-slate-500">Hindi share</div><div className="font-bold text-[#0F1B2D]">31%</div></div>
        <div><div className="text-slate-500">Marathi share</div><div className="font-bold text-[#0F1B2D]">8%</div></div>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// SECTION 5 — Fallback insights
// ---------------------------------------------------------------------------
const FALLBACK = [
  { id: "ai-1", severity: "green", title: "AI is replacing roughly 14 hrs of analyst work / week",
    body: "At 2,184 queries this month with 1.8s average response, the AI is doing the back-and-forth a junior analyst would otherwise handle. CSAT 4.8/5 across 487 ratings confirms accuracy.",
    action: "▶ Direct your team to use the AI for first-pass GST and margin questions before opening Tally." },
  { id: "ai-2", severity: "blue", title: "Multilingual is your founder advantage",
    body: "31% of queries land in Hindi and 8% in Marathi — your supervisors and stockists self-serve without needing English fluency. WhatsApp-native Q&A goes live next sprint.",
    action: "▶ Pin the Marathi AI link in the supervisor WhatsApp group this week." },
  { id: "ai-3", severity: "amber", title: "4 capabilities still in Beta — graduate them",
    body: "WhatsApp Q&A, GST reconciliation, voice board-report drafting and live ERP anomaly detection are all in Beta. Each one cuts an additional 3-5 hours of your weekly load.",
    action: "▶ Pick one Beta capability per week to test in production. Voice-to-board-report is highest ROI." },
  { id: "ai-4", severity: "blue", title: "Enterprise tier unlocks ₹1.2Cr revenue path",
    body: "Multi-entity consolidation and auto MCA filings (currently Enterprise) save 9 hours/quarter and become essential when you open plant 2. Plant 2 is in your 5yr trajectory.",
    action: "▶ Schedule an Enterprise demo when 5yr plan finalises." },
];

// ---------------------------------------------------------------------------
// Module shell
// ---------------------------------------------------------------------------
export default function AdvancedAI() {
  const { dataAvailable } = useCustomerData();

  // For active customers, dash all the live-telemetry KPIs (queries/response/CSAT
  // come from a usage log that isn't wired yet). The "Languages" and "AI Models"
  // KPIs describe platform capability, not telemetry, so they stay.
  const realKpis = KPIS.map((k) => {
    if (["queries", "response", "csat"].includes(k.id)) {
      return { ...k, sub: "Begins once you ask your first question" };
    }
    return k;
  });

  return (
    <div data-testid="adv-module" className="space-y-5 px-4 tablet:px-5 desktop:px-6 py-4 desktop:py-5">
      <ModuleHeader
        overline="Advanced Tools"
        title="One unified intelligence · across all of C-ORE"
        right={<><Brain size={13} className="text-emerald-600" />4 models · 3 languages · 12 capabilities</>}
      />

      <section data-testid="adv-kpis" className="grid grid-cols-2 gap-3 desktop:grid-cols-6">
        {realKpis.map((k) => (
          <KpiCard key={k.id} kpi={k} testidPrefix="adv-kpi" dataAvailable={dataAvailable}
            realValue={null} realText={null} />
        ))}
      </section>

      <div className="grid grid-cols-1 gap-5 tablet:grid-cols-2 desktop:grid-cols-3">
        <div className="xl:col-span-2"><CapabilityMatrix /></div>
        <div><MultilingualChat /></div>
      </div>

      <UsageBreakdown />

      <InsightsGrid
        subtitle="What your C-ORE advisor would tell you today"
        fallback={FALLBACK}
        fetchFn={api.advancedAiInsights}
        testidPrefix="adv"
        dataAvailable={dataAvailable}
      />
    </div>
  );
}
